// Lightweight i18n primitives. The actual phrase dictionaries live in
// src/i18n/locales/{en,es,pt}.ts and are loaded by LanguageContext:
// EN is bundled (default), ES/PT are dynamically imported on demand.
// Splitting cut ~70 KB of translated copy out of the main JS chunk.

import enDict from "./locales/en";

export type Lang = "en" | "es" | "pt";

export const LANGS: Lang[] = ["en", "es", "pt"];
export const DEFAULT_LANG: Lang = "en";

export const LANG_LABEL: Record<Lang, string> = {
  en: "EN",
  es: "ES",
  pt: "PT",
};

export const LANG_FULL: Record<Lang, string> = {
  en: "English",
  es: "Español",
  pt: "Português",
};

// Translation keys are derived from the English dictionary — every key must
// exist in en.ts, with parallel keys in es.ts / pt.ts.
export type TKey = keyof typeof enDict;

/** Synchronous English dictionary (always available, bundled). */
export const EN: Readonly<Record<TKey, string>> = enDict;

/**
 * Lazy loader for non-default languages. Returns a fully-typed dictionary.
 * The `import()` calls produce separate chunks named `locale-es` / `locale-pt`.
 */
export const loadLocale = async (
  lang: Lang,
): Promise<Readonly<Record<TKey, string>>> => {
  if (lang === "en") return enDict;
  if (lang === "es") {
    const m = await import("./locales/es");
    return m.default as Readonly<Record<TKey, string>>;
  }
  const m = await import("./locales/pt");
  return m.default as Readonly<Record<TKey, string>>;
};
